import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { API } from "../App";
import { useT } from "../i18n";
import LanguageToggle from "../components/LanguageToggle";
import ScoreGauge from "../components/ScoreGauge";
import SubScoreBar from "../components/SubScoreBar";
import KTBanner from "../components/KTBanner";
import PartnerAd from "../components/PartnerAd";
import { AlertTriangle, TrendingUp, Copy, Check, Gift, ArrowUpRight, Download, Mail, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import BonusCountdown from "../components/BonusCountdown";

const DIMS = [
  "scalp_balance","hydration_need","structural_damage","reconstruction_need",
  "heat_exposure","protection_need","buildup_tendency","shedding_concern",
];

export default function Plan({ lang, setLang, languages }) {
  const { resultId } = useParams();
  const tr = useT(lang);
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  const [copied, setCopied] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [emailSentTo, setEmailSentTo] = useState("");

  const sendReportEmail = async () => {
    setEmailSending(true);
    try {
      const body = emailTo.trim() ? { email: emailTo.trim(), lang } : { lang };
      const r = await axios.post(`${API}/assessments/${resultId}/email-report`, body);
      setEmailSentTo(r.data?.sent_to || (emailTo.trim() || "your inbox"));
      toast.success(lang === "it" ? "Report inviato!" : "Report sent!");
    } catch (err) {
      const code = err?.response?.data?.detail;
      let msg;
      if (code === "invalid_email") {
        msg = lang === "it" ? "Email non valida." : "Invalid email address.";
      } else if (code === "rate_limit_exceeded") {
        msg = lang === "it"
          ? "Troppi invii — attendi un'ora e riprova."
          : "Too many sends — please wait an hour and try again.";
      } else if (code === "email_service_unavailable") {
        msg = lang === "it"
          ? "Servizio email momentaneamente non disponibile."
          : "Email service temporarily unavailable.";
      } else {
        msg = lang === "it" ? "Impossibile inviare. Riprova." : "Couldn't send. Please try again.";
      }
      toast.error(msg);
    } finally {
      setEmailSending(false);
    }
  };

  useEffect(() => {
    axios.get(`${API}/assessments/${resultId}/full?lang=${lang}`)
      .then((r) => { setData(r.data); setStatus("ok"); })
      .catch((err) => {
        if (err?.response?.status === 402) setStatus("locked");
        else if (err?.response?.status === 404) setStatus("notfound");
        else setStatus("locked");
      });
  }, [resultId, lang]);

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success(tr.copied);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  if (status === "loading") return <div className="min-h-screen flex items-center justify-center text-[#6F6B66]">…</div>;
  if (status === "locked") return (
    <div className="min-h-screen flex items-center justify-center p-6" data-testid="plan-locked">
      <div className="text-center max-w-sm">
        <h2 className="font-serif text-2xl text-[#383633] mb-4">
          {lang === "it" ? "Piano non ancora sbloccato" : "Plan not yet unlocked"}
        </h2>
        <Link to={`/results/${resultId}`} className="inline-flex items-center gap-2 rounded-full bg-[#383633] text-white px-6 py-3 text-xs kt-tracking uppercase">
          {lang === "it" ? "Torna ai risultati" : "Back to results"}
        </Link>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center">Not found</div>;

  const credit = data.purchase_credit;
  const isMale = data.flow === "male";
  // Credit amount lives on the credit itself (issued at time of purchase, so it always
  // matches the price the user actually paid). This is the ONE source of truth for the
  // price shown on this page — no hardcoded values, no separate pricing lookup.
  const creditAmountFmt = credit && typeof credit.amount === "number"
    ? (lang === "it" ? credit.amount.toFixed(2).replace(".", ",") : credit.amount.toFixed(2))
    : "";

  return (
    <div className="min-h-screen bg-white" data-testid="plan-page">
      <div className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b kt-line">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between gap-3">
          <div className="block leading-tight" data-testid="hairtu-header">
            <Link to={`/results/${resultId}`} className="font-serif text-xl text-[#383633] tracking-tight hover:text-[#4A4238]">
              ← HAIRTU
            </Link>
            <div className="text-[9px] kt-tracking-wider uppercase text-[#6F6B66]">{tr.brand_powered}</div>
            <div className="text-[9px] kt-tracking-wider uppercase text-[#6F6B66]">{tr.brand_tagline}</div>
            <a
              href="https://www.ktmilano.com"
              target="_blank" rel="noreferrer"
              data-testid="header-ktmilano-link"
              className="text-[10px] text-[#6F6B66] hover:text-[#383633] underline decoration-[#D8C4A5] underline-offset-2"
            >
              {tr.brand_site}
            </a>
          </div>
          <div className="flex items-center gap-2">
            {data.unlock_reason === "salon" && (
              <Link
                to="/salon/dashboard"
                data-testid="plan-back-to-salon"
                className="inline-flex items-center gap-1.5 rounded-full bg-[#F8F6F2] hover:bg-[#EFE9DE] border kt-line text-[#383633] px-3 py-1.5 text-[10px] kt-tracking uppercase transition-colors"
              >
                ← {lang === "it" ? "Dashboard salone" : "Salon dashboard"}
              </Link>
            )}
            <LanguageToggle lang={lang} setLang={setLang} languages={languages} />
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-3">— KT Milano · Personalized Plan</div>
        <h1 className="font-serif text-4xl sm:text-5xl leading-tight text-[#383633] tracking-tight" data-testid="plan-heading">
          {data.first_name},<br />
          <span className="text-[#6F6B66]">
            {lang === "it" ? "ecco la tua analisi completa." : "here's your complete analysis."}
          </span>
        </h1>

        {/* Download My Full Report (PDF) — same paywall as this page. */}
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <a
            href={`${API}/assessments/${resultId}/report.pdf?lang=${lang}`}
            target="_blank"
            rel="noreferrer"
            data-testid="download-pdf-btn"
            className="inline-flex items-center gap-2 rounded-full border kt-line bg-white hover:bg-[#F8F6F2] text-[#383633] text-xs kt-tracking uppercase px-4 py-2 transition-colors"
          >
            <Download className="h-3.5 w-3.5" />
            {lang === "it" ? "Scarica il mio report completo (PDF)" : "Download My Full Report (PDF)"}
          </a>
        </div>

        {/* Email me my full report — sends the PDF as attachment via Resend. */}
        <div className="mt-6 rounded-3xl border kt-line bg-white p-5 sm:p-6 max-w-2xl" data-testid="email-report-card">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 rounded-full bg-[#F8F6F2] p-2">
              <Mail className="h-4 w-4 text-[#A8AD94]" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-serif text-lg text-[#383633] leading-tight">
                {lang === "it" ? "Ricevi il tuo report via email" : "Email me my full report"}
              </h3>
              <p className="text-sm text-[#6F6B66] mt-1 leading-relaxed">
                {lang === "it"
                  ? "Ti inviamo il PDF completo, incluso il bonus KT Milano, sull'email che preferisci. Usa l'email della tua valutazione oppure inserisci un altro indirizzo."
                  : "We'll email you the complete PDF, including your KT Milano bonus, at any address you choose. Leave the field empty to use the email on file."}
              </p>

              {emailSentTo ? (
                <div className="mt-4 flex items-center gap-2 text-sm text-[#5F7A5C]" data-testid="email-report-success">
                  <Check className="h-4 w-4" />
                  <span>
                    {lang === "it" ? "Inviato a" : "Sent to"} <span className="font-mono">{emailSentTo}</span>.{" "}
                    <button
                      type="button"
                      onClick={() => { setEmailSentTo(""); setEmailTo(""); }}
                      className="underline hover:text-[#383633]"
                      data-testid="email-report-again-btn"
                    >
                      {lang === "it" ? "Invia di nuovo" : "Send again"}
                    </button>
                  </span>
                </div>
              ) : (
                <form
                  onSubmit={(e) => { e.preventDefault(); if (!emailSending) sendReportEmail(); }}
                  className="mt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-2"
                >
                  <Input
                    type="email"
                    value={emailTo}
                    onChange={(e) => setEmailTo(e.target.value)}
                    placeholder={lang === "it" ? "usa l'email della tua valutazione o inseriscine un'altra" : "use email on file, or enter another"}
                    className="flex-1 rounded-full border kt-line bg-white text-sm px-4 py-2 focus-visible:ring-1 focus-visible:ring-[#A8AD94]"
                    data-testid="email-report-input"
                    disabled={emailSending}
                  />
                  <Button
                    type="submit"
                    disabled={emailSending}
                    data-testid="email-report-send-btn"
                    className="rounded-full bg-[#383633] hover:bg-[#4A4238] text-white text-xs kt-tracking uppercase px-5 py-2 transition-all disabled:opacity-60"
                  >
                    {emailSending ? (
                      <><Loader2 className="h-3.5 w-3.5 animate-spin" /> <span className="ml-2">{lang === "it" ? "Invio..." : "Sending..."}</span></>
                    ) : (
                      <><Mail className="h-3.5 w-3.5" /> <span className="ml-2">{lang === "it" ? "Invia il report" : "Send my report"}</span></>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Small KT Milano platform banner (removed the duplicate near top) */}

        {/* KT Milano Shopping Bonus — clearer presentation (spec #6) */}
        {credit && credit.code && (
          <div className="mt-10 rounded-3xl border kt-line bg-[#F8F6F2] p-6 sm:p-8" data-testid="credit-card">
            <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-2 flex items-center gap-2">
              <Gift className="h-3.5 w-3.5 text-[#A8AD94]" /> {tr.paid_credit_title}
            </div>
            <p className="text-sm text-[#383633] leading-relaxed max-w-xl mb-5" data-testid="credit-body">
              {(tr.paid_credit_body || "").replaceAll("{price}", creditAmountFmt)}
            </p>

            <div className="flex flex-wrap items-end gap-x-8 gap-y-3 mb-5">
              <div>
                <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66]">{tr.paid_credit_value_label}</div>
                <div className="font-serif text-3xl text-[#383633] mt-1 leading-none" data-testid="credit-value">
                  €{creditAmountFmt}
                </div>
              </div>
              <div>
                <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66]">{tr.paid_credit_expires_in}</div>
                <div className="text-sm text-[#383633] mt-1">
                  <BonusCountdown
                    expiresAt={credit.expires_at}
                    lang={lang}
                    expiredLabel={tr.paid_credit_expired}
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66]">{tr.paid_credit_code}</div>
                <div className="font-mono text-xl text-[#383633] mt-1 tracking-widest select-all break-all" data-testid="credit-code">
                  {credit.code}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" onClick={() => copyCode(credit.code)} data-testid="copy-credit-btn"
                  className="rounded-full border-[#383633] text-[#383633] hover:bg-white text-xs kt-tracking uppercase">
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  <span className="ml-2">{copied ? tr.paid_credit_code_copied : tr.paid_credit_code_copy}</span>
                </Button>
                <a
                  href={`https://www.ktmilano.com/?discount=${encodeURIComponent(credit.code)}&ref=hairtu_plan`}
                  target="_blank" rel="noreferrer"
                  data-testid="credit-shop-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-[#383633] hover:bg-[#4A4238] text-white text-xs kt-tracking uppercase px-4 py-2 transition-all"
                >
                  {tr.paid_credit_shop}
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              </div>
            </div>
          </div>
        )}

        {/* 01 · Snapshot — shared for women and men (adapts label + body) */}
        <div className="mt-12 grid sm:grid-cols-[auto,1fr] gap-8 sm:gap-12 items-center" data-testid="snapshot">
          <ScoreGauge value={data.overall_score} label={isMale ? (lang === "it" ? "Punteggio Cosmetico" : "Cosmetic Score") : tr.overall} />
          <div>
            <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-2">
              {lang === "it" ? "01 · La tua istantanea" : "01 · Your snapshot"}
            </div>
            {isMale ? (
              <>
                {data.profile_name && (
                  <div className="font-serif text-2xl text-[#383633] mb-2 leading-tight" data-testid="male-profile-name">
                    {data.profile_name}
                  </div>
                )}
                <p className="text-[#383633] text-base font-serif leading-relaxed" data-testid="profile-summary">
                  {data.main_concern}
                </p>
              </>
            ) : (
              <>
                <p className="text-[#383633] text-lg font-serif leading-relaxed" data-testid="profile-summary">
                  {data.one_sentence_profile || data.profile_summary}
                </p>
                {Array.isArray(data.three_priorities_v2) && data.three_priorities_v2.length > 0 && (
                  <ul className="mt-4 space-y-1.5" data-testid="hair-summary-bullets">
                    {data.three_priorities_v2.slice(0, 5).map((p, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#6F6B66]">
                        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#D8B589] shrink-0" />
                        <span><span className="text-[#383633]">{p.title}</span>{p.reason ? <> — {p.reason}</> : null}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        </div>

        {/* Three main priorities — now surfaced as the bullet list above; the routine below is the primary paid value */}

        {/* CATEGORY SCORES — shown for both women and men (men use dynamic dim list) */}
        {(() => {
          const dims = isMale
            ? (data.score_dims_order || []).filter((d) => (data.scores?.[d] || 0) > 0)
            : DIMS;
          const labelFor = (d) => isMale
            ? (data.score_dim_labels?.[d] || d)
            : tr.scores[d];
          if (!dims.length) return null;
          return (
        <div className="mt-14" data-testid="category-scores">
          <div className="flex items-end justify-between mb-4">
            <div>
              <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66]">
                {lang === "it" ? "02 · Punteggi per categoria" : "02 · Category scores"}
              </div>
              <h2 className="font-serif text-3xl text-[#383633] mt-1">
                {lang === "it" ? "Come hai ottenuto il tuo punteggio" : "How your score breaks down"}
              </h2>
            </div>
          </div>
          <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3 bg-white border kt-line rounded-2xl p-6 kt-shadow" data-testid="subscores">
            {dims.map((d) => {
              const v = data.scores[d] || 0;
              return (
                <li key={d} className="flex items-center gap-4 text-sm">
                  <span className="text-[#383633] w-44 shrink-0 truncate font-medium">{labelFor(d)}</span>
                  <span className="flex-1 h-2 rounded-full bg-[#F3F3F3] overflow-hidden">
                    <span className="block h-full bg-[#A8AD94] transition-all" style={{ width: `${v}%` }} />
                  </span>
                  <span className="w-10 text-right font-mono text-xs text-[#383633]">{v}</span>
                </li>
              );
            })}
          </ul>
        </div>
          );
        })()}

        {/* 03 · ESSENTIAL ROUTINE — 4 slots, prominent typography */}
        {data.routine_slots && Array.isArray(data.routine_slots.slots) && (
        <div className="mt-16" data-testid="routine-slots">
          <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66]">
            {lang === "it" ? "03 · La tua routine essenziale" : "03 · Your essential routine"}
          </div>
          <h2 className="font-serif text-3xl text-[#383633] mt-1 mb-2">
            {lang === "it" ? "Quattro passi, nell'ordine esatto" : "Four steps, in the exact order"}
          </h2>
          <p className="text-sm text-[#6F6B66] max-w-xl mb-6">
            {lang === "it"
              ? "Categorie di prodotto studiate sui tuoi tag — segui l'ordine da 1 a 4 per la tua routine di lavaggio."
              : "Product categories tuned to your tags — follow slots 1 → 4 as your wash-day routine."}
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            {data.routine_slots.slots.map((s) => (
              <div key={s.slot}
                className="bg-white border kt-line rounded-2xl p-6 kt-shadow"
                data-testid={`slot-${s.slot}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3">
                    <span className="h-9 w-9 rounded-full bg-[#D8B589] text-[#3A2C1B] flex items-center justify-center font-serif text-lg font-semibold">
                      {s.slot}
                    </span>
                    <div>
                      <div className="text-[11px] kt-tracking-wider uppercase text-[#6F6B66]">
                        {lang === "it" ? `Passo ${s.slot}` : `Step ${s.slot}`}
                      </div>
                      <div className="text-base font-semibold text-[#383633] leading-tight">
                        {s.role}
                      </div>
                    </div>
                  </div>
                  {s.frequency && (
                    <span className="shrink-0 text-[11px] font-medium px-2.5 py-1 rounded-full bg-[#F8F6F2] border kt-line text-[#383633]"
                      data-testid={`slot-${s.slot}-freq`}>
                      {s.frequency}
                    </span>
                  )}
                </div>
                <div className="font-serif text-xl text-[#383633] leading-snug mb-1">{s.category}</div>
                {s.formula && (
                  <div className="text-xs text-[#6F6B66] italic mb-3">{s.formula}</div>
                )}
                <p className="text-sm text-[#383633] leading-relaxed">{s.why}</p>
              </div>
            ))}
          </div>
        </div>
        )}

        {/* 04 · SIMPLE 4-WEEK PLAN — shared for women AND men (short male version) */}
        {Array.isArray(data.four_week_plan) && data.four_week_plan.length > 0 && (
        <div className="mt-20" data-testid="four-week-plan">
          <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66]">
            {lang === "it" ? "04 · Il tuo piano di 4 settimane" : "04 · Your simple 4-week plan"}
          </div>
          <h2 className="font-serif text-3xl text-[#383633] mt-1 mb-3">
            {lang === "it" ? "Un percorso guidato di 4 settimane" : "A guided 4-week journey"}
          </h2>
          <p className="text-sm text-[#6F6B66] max-w-xl mb-8">
            {lang === "it"
              ? "Segui il piano una settimana alla volta — cambiamenti visibili si costruiscono qui."
              : "Follow the plan one week at a time — visible change is built here."}
          </p>

          {/* Horizontal stepper indicator */}
          <div className="hidden sm:flex items-center gap-3 mb-6" aria-hidden="true">
            {data.four_week_plan.map((w, i) => (
              <div key={w.week} className="flex items-center gap-3 flex-1">
                <span className="h-8 w-8 rounded-full bg-[#A8AD94] text-white flex items-center justify-center font-mono text-xs font-semibold shrink-0">
                  {w.week}
                </span>
                {i < data.four_week_plan.length - 1 && (
                  <span className="flex-1 h-px bg-[#D8B589] opacity-60" />
                )}
              </div>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {data.four_week_plan.map((w) => (
              <div key={w.week}
                className="bg-white border kt-line rounded-2xl p-6 kt-shadow flex flex-col"
                data-testid={`week-${w.week}`}>
                <div className="text-[10px] kt-tracking-wider uppercase text-[#D8B589] font-semibold mb-2">
                  {lang === "it" ? `Settimana ${w.week} di ${data.four_week_plan.length}` : `Week ${w.week} of ${data.four_week_plan.length}`}
                </div>
                <div className="font-serif text-xl text-[#383633] mb-3 leading-snug">{w.title}</div>
                <p className="text-sm text-[#6F6B66] leading-relaxed">{w.body}</p>
              </div>
            ))}
          </div>
        </div>
        )}

        {/* Male-flow guidance cards moved to the very end (see below) — keep numbered flow clean */}

        {/* KT Milano recommendation banner — clickable, goes to www.ktmilano.com */}
        <div className="mt-16" data-testid="kt-recommendation-banner">
          <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-4">
            {tr.recs_title}
          </div>
          <KTBanner size="large" lang={lang} />
        </div>

        {/* 05 · Product-type routine — shared for women (9 steps) and men (6 steps) */}
        {data.routine && Array.isArray(data.routine.steps) && data.routine.steps.length > 0 && (
        <div className="mt-16" data-testid="routine-v2">
          <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66]">
            {lang === "it" ? "05 · La tua routine per categorie" : "05 · Your product-type routine"}
          </div>
          <h2 className="font-serif text-3xl text-[#383633] mt-1 mb-2">
            {isMale
              ? (lang === "it" ? "6 passi essenziali, prodotto per prodotto" : "6 essential steps, product-by-product")
              : (lang === "it" ? "Il dettaglio prodotto per prodotto" : "The full product-type breakdown")}
          </h2>
          <p className="text-sm text-[#6F6B66] max-w-lg mb-6">
            {lang === "it"
              ? "Categorie di prodotto neutrali — sequenza pensata per i tuoi capelli."
              : "Brand-neutral product categories — in the exact order your hair needs."}
          </p>
          <ol className="space-y-3">
            {data.routine.steps.map((s, i) => (
              <li key={i}
                className={`border kt-line rounded-2xl p-4 sm:p-5 ${s.included === false ? "bg-[#F8F6F2] opacity-70" : "bg-white"}`}
                data-testid={`routine-step-${s.step ?? i + 1}`}>
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-7 h-7 rounded-full bg-[#F3F3F3] border kt-line flex items-center justify-center text-xs font-mono text-[#383633]">{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    {s.label && <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66]">{s.label}</div>}
                    <div className="font-serif text-base sm:text-lg text-[#383633] leading-tight mt-0.5">{s.category}</div>
                    {s.formula && <div className="text-xs text-[#6F6B66] mt-1">{s.formula}</div>}
                    {s.role && !s.formula && <div className="text-xs text-[#6F6B66] mt-1">{s.role}</div>}
                    {s.frequency && s.frequency !== "—" && (
                      <div className="text-[11px] text-[#A8AD94] mt-1 uppercase kt-tracking">{s.frequency}</div>
                    )}
                    {s.included === false && (
                      <div className="text-[10px] text-[#6F6B66] mt-1 italic">
                        {lang === "it" ? "Non incluso in questo ciclo" : "Not included this cycle"}
                      </div>
                    )}
                    {s.why && (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-[11px] uppercase kt-tracking text-[#383633] hover:text-[#4A4238]">
                          {lang === "it" ? "Perché" : "Why"}
                        </summary>
                        <p className="text-sm text-[#6F6B66] leading-relaxed mt-2">{s.why}</p>
                      </details>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
        )}

        {/* Mistakes — shared for women (string[]) and men ({title, body}[]) */}
        {Array.isArray(data.mistakes_to_avoid) && data.mistakes_to_avoid.length > 0 && (
        <div className="mt-20">
          <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-6 flex items-center gap-2">
            <AlertTriangle className="h-3 w-3" /> {tr.mistakes_title || (lang === "it" ? "Errori da evitare" : "Mistakes to avoid")}
          </div>
          <ul className="space-y-3" data-testid="mistakes">
            {data.mistakes_to_avoid.map((m, i) => {
              const isObj = typeof m === "object" && m !== null;
              return (
                <li key={i} className="bg-white border kt-line rounded-xl p-4 text-sm text-[#6F6B66] leading-relaxed flex gap-3">
                  <span className="text-[#A8AD94] font-mono text-xs mt-0.5 shrink-0">0{i + 1}</span>
                  {isObj ? (
                    <div>
                      <div className="text-[#383633] font-medium">{m.title}</div>
                      <div className="mt-1">{m.body}</div>
                    </div>
                  ) : (
                    <span>{m}</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
        )}

        {/* Progress · What you can expect — shared for women and men */}
        {Array.isArray(data.progress_tips) && data.progress_tips.length > 0 && (
        <div className="mt-20">
          <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-6 flex items-center gap-2">
            <TrendingUp className="h-3 w-3" /> {tr.progress_title || (lang === "it" ? "Cosa puoi aspettarti" : "What you can expect")}
          </div>
          <ul className="space-y-3" data-testid="progress">
            {data.progress_tips.map((m, i) => (
              <li key={i} className="bg-[#F8F6F2] border kt-line rounded-xl p-4 text-sm text-[#383633] leading-relaxed">{m}</li>
            ))}
          </ul>
        </div>
        )}

        {/* Male-only "The rest" — daily / wash-day / styling guidance moved to end */}
        {isMale && (
          <div className="mt-20" data-testid="male-paid-block">
            <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-6">
              {lang === "it" ? "Il resto · Guida quotidiana" : "The rest · Daily guidance"}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-white border kt-line rounded-2xl p-5">
                <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-2">
                  {lang === "it" ? "Ogni mattina" : "Morning / daily"}
                </div>
                <p className="text-sm text-[#383633] leading-relaxed" data-testid="male-morning">{data.morning_guidance}</p>
              </div>
              <div className="bg-white border kt-line rounded-2xl p-5">
                <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-2">
                  {lang === "it" ? "Giorno di lavaggio" : "Wash day"}
                </div>
                <p className="text-sm text-[#383633] leading-relaxed" data-testid="male-washday">{data.wash_day_guidance}</p>
              </div>
              <div className="bg-white border kt-line rounded-2xl p-5">
                <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-2">
                  {lang === "it" ? "Styling & residui" : "Styling & buildup"}
                </div>
                <p className="text-sm text-[#383633] leading-relaxed" data-testid="male-styling">{data.styling_buildup_guidance}</p>
              </div>
              <div className="sm:col-span-2 bg-[#F8F6F2] border kt-line rounded-2xl p-5">
                <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-2">
                  {lang === "it" ? "Quando semplificare" : "When to simplify / adjust"}
                </div>
                <p className="text-sm text-[#383633] leading-relaxed" data-testid="male-adjust">{data.adjust_when}</p>
              </div>
            </div>
          </div>
        )}

        {/* Dermatologist notice — male flow only */}
        {isMale && data.dermatologist_notice && (
          <div className="mt-16 bg-white border kt-line rounded-2xl p-5 text-[11px] text-[#6F6B66] leading-relaxed" data-testid="male-derm-notice">
            {data.dermatologist_notice}
          </div>
        )}

        {/* Optional: day-by-day 30-day plan — secondary to the 4-week blocks above */}
        <details className="mt-16 bg-white border kt-line rounded-2xl p-5" data-testid="thirty-day-plan-wrap">
          <summary className="cursor-pointer text-xs kt-tracking uppercase text-[#383633] flex items-center justify-between">
            <span>{tr.plan_title}</span>
            <span className="text-[10px] text-[#6F6B66] normal-case">
              {lang === "it" ? "Vista giornaliera" : "Day-by-day view"}
            </span>
          </summary>
          <div className="grid gap-3 mt-6" data-testid="thirty-day-plan">
            {data.plan_30_days.map((d, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.3, delay: (i % 6) * 0.02 }}
                className="bg-[#F8F6F2] border kt-line rounded-xl p-4 flex gap-4"
                data-testid={`plan-day-${d.day}`}
              >
                <div className="shrink-0 w-12">
                  <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66]">{tr.day}</div>
                  <div className="font-serif text-2xl text-[#383633]">{String(d.day).padStart(2, "0")}</div>
                </div>
                <div className="min-w-0">
                  <div className="font-serif text-base text-[#383633] tracking-tight mb-0.5">{d.title}</div>
                  <div className="text-sm text-[#6F6B66] leading-relaxed">{d.body}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </details>

        {/* Correct ending order:
            1. Product recos (above)
            2. 30-day plan (above)
            3. Small KT Milano banner
            4. Optional partner banner
            5. Privacy / cosmetic disclaimer */}
        <div className="mt-12">
          <KTBanner size="small" lang={lang} />
        </div>

        <PartnerAd
          placement="paid_plan"
          language={lang}
          audience={isMale ? "male" : "female"}
          profile={isMale ? (data.profile_name || "") : ((data.top_priorities && data.top_priorities[0]?.title) || "")}
        />

        <div className="mt-10 text-[10px] text-[#6F6B66] leading-relaxed border-t kt-line pt-6">
          {tr.footer_disclaimer}
        </div>
      </div>
    </div>
  );
}
